/**
 * O que faz: Exibe uma sugestão de melhoria de prompt gerada pela IA com diff lado a lado
 *            e botões para aplicar ou ignorar.
 * Onde usa: ChatAnalysisSection quando há sugestões com status 'pendente'.
 * Conexões: useApplyPromptSuggestion, useDismissPromptSuggestion.
 * IMPORTANTE: A sugestão NUNCA é aplicada automaticamente. Requer ação do usuário.
 * Autor: IA
 * Data: 24/04/2026
 */

import { useState } from 'react';
import { Check, ChevronDown, ChevronUp, Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useApplyPromptSuggestion, useDismissPromptSuggestion } from '@/hooks/useChatAnalysis';
import type { PromptImprovementSuggestion } from '@/types/chatAnalysis';

interface PromptSuggestionCardProps {
  suggestion: PromptImprovementSuggestion;
  clinicaId: string;
  readOnly?: boolean;
}

export const PromptSuggestionCard = ({
  suggestion,
  clinicaId,
  readOnly = false,
}: PromptSuggestionCardProps) => {
  const [showDiff, setShowDiff] = useState(false);
  const apply = useApplyPromptSuggestion(clinicaId);
  const dismiss = useDismissPromptSuggestion(clinicaId);

  return (
    <Card className="rounded-xl border border-warning/30 bg-warning-subtle/50 shadow-sm">
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-warning-subtle">
              <Sparkles className="h-4 w-4 text-warning-text" />
            </div>
            <div>
              <p className="text-sm font-semibold text-warning-text">
                Sugestão de melhoria no prompt de atendimento
              </p>
              <p className="text-xs text-warning-text">
                Gerada em {new Date(suggestion.criado_em).toLocaleString('pt-BR', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Lista de mudanças */}
        {suggestion.mudancas_resumidas.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-warning-text">O que mudaria:</p>
            <ul className="space-y-1">
              {suggestion.mudancas_resumidas.map((change, i) => (
                <li key={i} className="flex items-start gap-1.5 text-xs text-warning-text">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-warning" />
                  {change}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Diff expandível */}
        <button
          type="button"
          className="flex w-full items-center gap-1 text-xs text-warning-text hover:text-warning-text"
          onClick={() => setShowDiff((v) => !v)}
        >
          {showDiff ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {showDiff ? 'Ocultar' : 'Ver'} prompts completos
        </button>

        {showDiff && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Atual</p>
              <pre className="max-h-48 overflow-y-auto rounded-lg border bg-white p-3 text-xs whitespace-pre-wrap text-muted-foreground">
                {suggestion.prompt_atual || '(sem prompt definido)'}
              </pre>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-accent-foreground">Sugerido</p>
              <pre className="max-h-48 overflow-y-auto rounded-lg border border-border bg-accent p-3 text-xs whitespace-pre-wrap text-foreground">
                {suggestion.prompt_sugerido}
              </pre>
            </div>
          </div>
        )}

        {/* Ações */}
        {!readOnly && (
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              className="rounded-full"
              onClick={() => apply.mutate(suggestion.id)}
              disabled={apply.isPending || dismiss.isPending}
            >
              {apply.isPending ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <Check className="mr-1 h-3 w-3" />
              )}
              Aplicar sugestão
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => dismiss.mutate(suggestion.id)}
              disabled={apply.isPending || dismiss.isPending}
            >
              {dismiss.isPending ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <X className="mr-1 h-3 w-3" />
              )}
              Ignorar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
